import type { AttributionIdentifiers } from './types';
import { ClickIdParser } from './ClickIdParser';
import { CookieCollector } from './CookieCollector';

/**
 * Collects attribution signals from URL + cookies and merges them.
 */
export class AttributionCollector {
  private readonly clickIdParser = new ClickIdParser();

  private readonly cookieCollector = new CookieCollector();

  private readonly customParams: readonly string[];

  private readonly captureUrlParams: boolean;

  private readonly captureCookies: boolean;

  constructor(
    options: {
      customParams?: readonly string[];
      captureUrlParams?: boolean;
      captureCookies?: boolean;
    } = {}
  ) {
    this.customParams = options.customParams ?? [];
    this.captureUrlParams = options.captureUrlParams !== false;
    this.captureCookies = options.captureCookies !== false;
  }

  collectFresh(): Partial<AttributionIdentifiers> {
    const fromUrl = this.captureUrlParams
      ? this.clickIdParser.parse(this.customParams)
      : {};
    const fromCookies = this.captureCookies
      ? this.cookieCollector.collectKnown()
      : {};

    const merged: Record<string, unknown> = {
      ...fromCookies,
      ...fromUrl,
    };

    const customClickIds = {
      ...((fromCookies as AttributionIdentifiers).customClickIds ?? {}),
      ...(fromUrl.customClickIds ?? {}),
    };
    if (Object.keys(customClickIds).length > 0) {
      merged.customClickIds = customClickIds;
    }

    if (typeof merged.fbclid === 'string' && merged.fbclid) {
      const fbc = this.clickIdParser.synthesizeFbc(
        merged.fbclid,
        typeof merged.fbc === 'string' ? merged.fbc : undefined
      );
      if (fbc) merged.fbc = fbc;
    }

    if (typeof window !== 'undefined') {
      try {
        merged.landingUrl = window.location.href;
      } catch {
        // ignore
      }
    }

    if (typeof document !== 'undefined') {
      try {
        if (document.referrer) merged.referrer = document.referrer;
      } catch {
        // ignore
      }
    }

    return merged as Partial<AttributionIdentifiers>;
  }

  /**
   * Merge: never overwrite existing defined values with undefined.
   */
  merge(
    existing: AttributionIdentifiers,
    incoming: Partial<AttributionIdentifiers>
  ): AttributionIdentifiers {
    const next: Record<string, unknown> = { ...existing };

    for (const [key, value] of Object.entries(incoming)) {
      if (value === undefined || value === null || value === '') continue;

      if (key === 'customClickIds' && typeof value === 'object') {
        next.customClickIds = {
          ...((existing.customClickIds as Record<string, string>) ?? {}),
          ...(value as Record<string, string>),
        };
        continue;
      }

      next[key] = value;
    }

    const now = Date.now();
    if (!existing.firstCollectedAt) {
      next.firstCollectedAt = now;
    }
    next.lastCollectedAt = now;

    return next as AttributionIdentifiers;
  }
}
